import { action, flow } from 'mobx';
import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, StoreContainer } from 'bernie-core';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { Localization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { PageData } from 'bernie-http';

import {
  FlightInformationStore,
  FreeCancellationStore,
  InformationCriteriaStore,
  InformationResultStore,
  MessagingCardsStore,
  BannersStore,
  PriceSummaryStore,
  ErrorStore,
  ClientMetadataStore,
  PageTitleStore,
  FlightsSelectedJourneyReviewStore,
  OpinionLabStore,
  StepIndicatorStore,
  DetailsAndFaresStore,
  GaiaIdStore,
  JourneyStartEndDatesStore,
  JourneyContinuationIdStore,
  FlightsSupportedAncillariesStore,
  FlightsInfoPriceMatchPromiseStore,
  VirtualAgentStore,
  PartnerEarnCardStore
} from 'stores';
import { generateRequestOptions, needSearchFis, needSearchBasisOnInfoKeys, PageId, getPageName } from 'utility';
import { FlightsQueryState, FlightsShoppingPwaFlightInformationResults } from '__generated__/api';
import { DynamicElementsStore } from 'stores/dynamic-elements-store';
import { DetailsController } from 'controllers/details-controller';
import { getNavigationIdFromQuery } from 'utility/navigation-identity';
import { AdditionalShoppingStore } from 'stores/additional-shopping-store';

export class FlightInformationController implements Controller {
  public pageId = PageId.FLIGHT_INFORMATION;
  public path = '/Flight-Information';
  public routeName = 'flight-information';
  public bundles = [];
  public exact = true;
  public component = codeSplit(() => import(/* webpackChunkName: "information" */ '../views/flight-information'));
  public pendingPromises = [];
  public clientErrorRenderDelay = 500;
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightInformation');
  public firstCall = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetch(options: FetchOptions): Promise<any> {
    if (options.isServer) {
      return this.fetchServerSide(options);
    }
    const searchNeeded = needSearchFis(options, false) && needSearchBasisOnInfoKeys(options.query);
    if (searchNeeded && this.firstCall) {
      return this.fetchClientSide(options);
    }

    const bannersStore = options.stores.get<BannersStore>('bannersStore');
    bannersStore.setIsDialogEvent(!searchNeeded);

    return Promise.resolve();
  }

  public fetchPageData(pageData: FetchPageOptions): Promise<PageData> {
    const localizer = new Localization(pageData.context.locale);
    const query = pageData.query;
    const title = localizer.formatText('fis.server.page.title');
    const pageName = getPageName(this.pageId);
    const navigationId = getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageId: this.pageId, pageName, navigationId });
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      flightInformationStore,
      informationResultStore,
      analyticsStore,
      freeCancellationStore,
      informationCriteriaStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      variables,
      flightsSelectedJourneyReviewStore,
      stepIndicatorStore,
      gaiaIdStore,
      journeyStartEndDatesStore,
      flightsSupportedAncillariesStore,
      dynamicElementsStore,
      virtualAgentStore,
      additionalShoppingStore
    } = this.getFetchVariables(options.stores, options.isServer);
    virtualAgentStore.isVirtualAgentClient = options?.query?.client === 'virtual-agent';
    informationCriteriaStore.setCriteriaFromUrl(options);
    const response = await informationResultStore.fetchLoading(
      variables,
      generateRequestOptions(options.request, options.isServer, this.pageId),
      analyticsStore
    );

    if (!response) {
      return;
    }

    if (options?.query?.identifier) {
      dynamicElementsStore.setSelectedIdentifier(options.query.identifier);
    }

    DetailsController.hydrateDetailsStores(
      response,
      freeCancellationStore,
      flightsSelectedJourneyReviewStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      stepIndicatorStore,
      gaiaIdStore,
      journeyStartEndDatesStore,
      flightsSupportedAncillariesStore,
      additionalShoppingStore
    );

    flightInformationStore.loading = true;
    errorStore.noApiResponse = false;
  }

  private fetchClientSide(options: FetchOptions) {
    if (this.pendingPromises.length > 0) {
      this.pendingPromises.forEach(p => p.cancel());
    }

    const clientPromise = this.createFlowForClientFetch(options, this.pageId);

    this.pendingPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForClientFetch = flow(function* (options: FetchOptions, pageId: string) {
    const {
      flightInformationStore,
      informationResultStore,
      analyticsStore,
      messagingCardsStore,
      informationCriteriaStore,
      freeCancellationStore,
      priceSummaryStore,
      bannersStore,
      errorStore,
      clientMetadataStore,
      variables,
      pageTitleStore,
      flightsSelectedJourneyReviewStore,
      dynamicElementsStore,
      opinionLabStore,
      detailsAndFaresStore,
      journeyContinuationIdStore,
      flightsInfoPriceMatchPromiseStore,
      partnerEarnCardStore
    } = this.getFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageId);
    informationCriteriaStore.setCriteriaFromUrl(options);
    flightInformationStore.loading = true;
    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaFlightInformationResults.FlightsDetail = yield informationResultStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );

      if (!response || Object.keys(response).length == 0 || response.serverErrorMessaging) {
        errorStore.setClientFetchFailure(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FIS_FETCH_FAILED'));

        return;
      }
      const messagingCards = response?.messagingCards;
      const banners = response?.banners;
      const clientMetadata = response?.clientMetadata;
      const flightsSelectedJourneyReview = response?.flightsSelectedJourneyReview;
      const dynamicElementsGroups = response?.dynamicElementsGroups;
      const partnerLoyaltyEarnMessage = response.partnerLoyaltyEarnMessage;
      const {
        freeCancellation,
        separateTicketBanner,
        priceSummary,
        serverErrorMessaging,
        pageTitle,
        analytics,
        flightsOpinionLab,
        multiItemDynamicCrossSellPrimer,
        journeyContinuationId,
        baseOfferAncillaryID,
        flightsInformationPriceMatchPromise,
        smartShoppingExperienceEligibility
      } = response;
      freeCancellationStore.hydrate({ freeCancellation });
      messagingCardsStore.hydrate({ messagingCards, separateTicketBanner });
      priceSummaryStore.hydrate({ priceSummary });
      bannersStore.hydrate({ banners });
      errorStore.hydrate({ serverErrorMessaging });
      flightsSelectedJourneyReviewStore.hydrate({ flightsSelectedJourneyReview });
      pageTitleStore.hydrate({ pageTitle });
      clientMetadataStore.hydrate({ clientMetadata });
      opinionLabStore.hydrate(flightsOpinionLab);
      flightInformationStore.hydrate({
        analytics,
        multiItemDynamicCrossSellPrimer,
        baseOfferAncillaryID,
        smartShoppingExperienceEligibility
      });
      journeyContinuationIdStore.hydrate({ journeyContinuationId });
      partnerEarnCardStore.hydrate({ partnerLoyaltyEarnMessage });
      dynamicElementsStore.hydrate({ dynamicElementsGroups: dynamicElementsGroups });
      dynamicElementsStore.initialiseDynamicStoreCache();
      if (options && options.query && options.query.identifier) {
        dynamicElementsStore.setSelectedIdentifier(options.query.identifier);
        priceSummaryStore.setPriceSummary(dynamicElementsStore.getPriceSummaryById(options.query.identifier));
        flightsSelectedJourneyReviewStore.setFlightsSelectedJourneyReview(
          dynamicElementsStore.getFlightsJourneyDetailsById(options.query.identifier)
        );
        detailsAndFaresStore.setAppliedFareType(options.query.identifier);
      }
      if (options?.query?.journeyContinuationId !== journeyContinuationId) {
        flightInformationStore.priceChange = true;
      }
      flightsSelectedJourneyReviewStore.initialiseDynamicElementsIdentifier();
      //TODO: https://jira.expedia.biz/browse/AIRMRCH-92
      priceSummaryStore.setSeatSelectionLoadingSkeletonCount(
        flightsSelectedJourneyReviewStore.flightsSelectedJourneyReviewItem(0)?.fareDetails?.fareSeatMapInformation
          ?.skeletonCount
      );
      pageTitleStore.loading = false;
      informationCriteriaStore.queryState = FlightsQueryState.LOADED;
      flightInformationStore.loading = false;
      this.pendingPromises = [];
      flightsInfoPriceMatchPromiseStore.hydrate({ priceMatchPromise: flightsInformationPriceMatchPromise });

      return;
    } catch (error) {
      errorStore.setClientFetchFailure(true);
      this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FIS_CLIENT_HYDRATION'), error);
    }
  });

  private getFetchVariables(stores: StoreContainer, isServer: boolean) {
    const informationCriteriaStore = stores.get<InformationCriteriaStore>('informationCriteriaStore');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    informationCriteriaStore.informationCriteria.queryState = loadSwitch;
    const variables = { context, ...informationCriteriaStore.informationCriteria };
    const flightInformationStore = stores.get<FlightInformationStore>('flightInformationStore');
    const informationResultStore = stores.get<InformationResultStore>('informationResult');
    const freeCancellationStore = stores.get<FreeCancellationStore>('freeCancellationStore');
    const messagingCardsStore = stores.get<MessagingCardsStore>('messagingCardsStore');
    const bannersStore = stores.get<BannersStore>('bannersStore');
    const priceSummaryStore = stores.get<PriceSummaryStore>('priceSummaryStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const pageTitleStore = stores.get<PageTitleStore>('pageTitleStore');
    const opinionLabStore = stores.get<OpinionLabStore>('opinionLabStore');
    const gaiaIdStore = stores.get<GaiaIdStore>('gaiaIdStore');
    const journeyStartEndDatesStore = stores.get<JourneyStartEndDatesStore>('journeyStartEndDatesStore');
    const flightsSelectedJourneyReviewStore = stores.get<FlightsSelectedJourneyReviewStore>(
      'flightsSelectedJourneyReviewStore'
    );
    const dynamicElementsStore = stores.get<DynamicElementsStore>('dynamicElementsStore');
    const stepIndicatorStore = stores.get<StepIndicatorStore>('stepIndicatorStore');
    const detailsAndFaresStore = stores.get<DetailsAndFaresStore>('detailsAndFaresStore');
    const journeyContinuationIdStore = stores.get<JourneyContinuationIdStore>('journeyContinuationIdStore');
    const flightsSupportedAncillariesStore = stores.get<FlightsSupportedAncillariesStore>(
      'flightsSupportedAncillariesStore'
    );
    const flightsInfoPriceMatchPromiseStore = stores.get<FlightsInfoPriceMatchPromiseStore>(
      'flightsInfoPriceMatchPromiseStore'
    );
    const virtualAgentStore = stores.get<VirtualAgentStore>('virtualAgentStore');
    const partnerEarnCardStore = stores.get<PartnerEarnCardStore>('partnerEarnCardStore');
    const additionalShoppingStore = stores.get<AdditionalShoppingStore>('additionalShoppingStore');

    return {
      analyticsStore,
      variables,
      flightInformationStore,
      informationResultStore,
      informationCriteriaStore,
      freeCancellationStore,
      messagingCardsStore,
      bannersStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      opinionLabStore,
      flightsSelectedJourneyReviewStore,
      dynamicElementsStore,
      stepIndicatorStore,
      detailsAndFaresStore,
      gaiaIdStore,
      journeyStartEndDatesStore,
      journeyContinuationIdStore,
      flightsSupportedAncillariesStore,
      flightsInfoPriceMatchPromiseStore,
      virtualAgentStore,
      partnerEarnCardStore,
      additionalShoppingStore
    };
  }
}
